import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

function hexToHsl(hex) {
  if (!hex || !hex.startsWith("#")) return null;
  let r = 0, g = 0, b = 0;
  if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  } else return null;
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyDesign(design) {
  if (!design) return;
  const root = document.documentElement;
  const map = {
    primary: "--primary", background: "--background", surface: "--card",
    accent: "--accent", border: "--border", muted: "--muted",
  };
  Object.entries(map).forEach(([k, cssVar]) => {
    const hsl = hexToHsl(design[k]);
    if (hsl) root.style.setProperty(cssVar, hsl);
  });
  if (design.surface) root.style.setProperty("--popover", hexToHsl(design.surface));
  if (design.radius) root.style.setProperty("--radius", design.radius);
  if (design.heading_font) root.style.setProperty("--heading-font", `'${design.heading_font}', sans-serif`);
  if (design.body_font) root.style.setProperty("--body-font", `'${design.body_font}', sans-serif`);
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("b1m_cart") || "[]"); } catch { return []; }
  });
  const [design, setDesign] = useState(null);
  const [settings, setSettings] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => { localStorage.setItem("b1m_cart", JSON.stringify(cart)); }, [cart]);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("b1m_token");
    if (!token) { setAuthChecked(true); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch { localStorage.removeItem("b1m_token"); setUser(null); }
    setAuthChecked(true);
  }, []);

  const loadFavorites = useCallback(async () => {
    if (!localStorage.getItem("b1m_token")) { try { setFavorites(JSON.parse(localStorage.getItem("b1m_favs") || "[]")); } catch { setFavorites([]); } return; }
    const local = (() => { try { return JSON.parse(localStorage.getItem("b1m_favs") || "[]"); } catch { return []; } })();
    if (local.length) { await Promise.all(local.map((f) => api.post(`/favorites/${f.id}`).catch(() => {}))); localStorage.removeItem("b1m_favs"); }
    try { const { data } = await api.get("/favorites"); setFavorites(data); } catch (e) { if (process.env.NODE_ENV !== "production") console.error("Kunne ikke hente favoritter:", e); }
  }, []);

  useEffect(() => {
    loadMe();
    api.get("/design").then(({ data }) => { setDesign(data); applyDesign(data); }).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [loadMe]);

  useEffect(() => { loadFavorites(); }, [user, loadFavorites]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("b1m_token", data.token);
    setUser(data.user);
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("b1m_token", data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = () => { localStorage.removeItem("b1m_token"); setUser(null); };

  const addToCart = (product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) => i.id === product.id ? { ...i, quantity: Math.min(i.quantity + qty, product.stock) } : i);
      }
      return [...prev, {
        id: product.id, name: product.name, price: product.effective_price ?? product.price,
        image: product.main_image, slug: product.slug, stock: product.stock, quantity: qty,
      }];
    });
    setCartOpen(true);
  };
  const updateQty = (id, qty) => setCart((prev) => prev.map((i) => i.id === id ? { ...i, quantity: Math.max(1, qty) } : i));
  const removeFromCart = (id) => setCart((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => setCart([]);
  const oneMore = (product, evt) => {
    if (!product.in_stock && product.stock <= 0) return;
    const inCart = cart.find((i) => i.id === product.id);
    if (inCart) setCart((prev) => prev.map((i) => (i.id === product.id ? { ...i, quantity: Math.min(i.quantity + 1, product.stock) } : i)));
    else setCart((prev) => [...prev, { id: product.id, name: product.name, price: product.effective_price ?? product.price, image: product.main_image, slug: product.slug, stock: product.stock, quantity: 1 }]);
    const r = evt?.currentTarget?.getBoundingClientRect?.();
    window.dispatchEvent(new CustomEvent("b1m:onemore", { detail: { x: r ? r.left + r.width / 2 : window.innerWidth / 2, y: r ? r.top + r.height / 2 : window.innerHeight / 2 } }));
  };
  const [surpriseOpen, setSurpriseOpen] = useState(false);

  const toggleFavorite = async (product) => {
    const isFav = favorites.some((f) => f.id === product.id);
    if (!user) {
      const next = isFav ? favorites.filter((f) => f.id !== product.id) : [...favorites, product];
      setFavorites(next); localStorage.setItem("b1m_favs", JSON.stringify(next)); return !isFav;
    }
    if (isFav) { await api.delete(`/favorites/${product.id}`); setFavorites((p) => p.filter((f) => f.id !== product.id)); }
    else { await api.post(`/favorites/${product.id}`); setFavorites((p) => [...p, product]); }
    return !isFav;
  };

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const refreshDesign = (d) => { setDesign(d); applyDesign(d); };

  return (
    <AppContext.Provider value={{
      user, authChecked, login, register, logout, loadMe,
      cart, cartCount, cartSubtotal, addToCart, updateQty, removeFromCart, clearCart,
      cartOpen, setCartOpen,
      design, setDesign: refreshDesign, settings, setSettings,
      favorites, toggleFavorite, loadFavorites, oneMore, surpriseOpen, setSurpriseOpen,
    }}>
      {children}
    </AppContext.Provider>
  );
}
